import { useEffect, useRef } from 'react'

import { useEditGateway } from '../hooks/useEditGateway'
import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Button, Flex, Modal, Text } from '@hivewatch/dark'

import {
  ControlledMultiSelectInput,
  ControlledTextInput,
  SkeletonModalBody,
} from '@/components/ui'
import { UpdateEdgeDeviceInput } from '@/graphql/generated/schemas'

import { GatewayFormInputsIProps } from '../utils/utils'

interface DarkEditGatewayModalIProps {
  id: string
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link EditGatewayModal} built on `@hivewatch/dark`.
 * Same query, mutation, validation and permission gating — only the chrome
 * and the two fields move to the design system. Rendered from the dark
 * gateways table (`DarkGatewaysTableModal`); the legacy table keeps the
 * legacy modal.
 */
export const DarkEditGatewayModal = ({
  id,
  isOpen,
  onClose,
}: DarkEditGatewayModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableUpdateGateway } = usePermissions()
  const isEditMode = useIsEditModeSatisfied()
  const { showSuccess, showError } = useToasts()
  const {
    deviceData,
    devicesOptions,
    devices,
    update,
    isUpdateLoading,
    isDeviceLoading,
    isLoading: isDevicesOptionsLoading,
  } = useEditGateway(id)

  const { handleSubmit, control, errors, setValue } =
    useForm<GatewayFormInputsIProps>({
      reValidateMode: 'onChange',
    })
  const selectedDevices = useWatch({
    control,
    name: 'devices',
    defaultValue: [],
  })
  const seededGatewayIdRef = useRef<string>()
  const hasCurrentDeviceData = deviceData?.edgeDevice?.id === id

  // Seed once per gateway so background refetches do not overwrite user edits.
  useEffect(() => {
    if (hasCurrentDeviceData && seededGatewayIdRef.current !== id) {
      setValue('devices', devices)
      seededGatewayIdRef.current = id
    }
  }, [devices, hasCurrentDeviceData, id, setValue])

  const onSubmit: SubmitHandler<GatewayFormInputsIProps> = async (values) => {
    const name = values.name
    const deviceIds = values.devices?.map((d) => d.value) || []
    const formData: UpdateEdgeDeviceInput = { id, name, deviceIds }

    try {
      await update(formData)
      showSuccess(t('gateways:crud_edit_success'))
      onClose()
    } catch (error) {
      showError(error)
    }
  }

  // Gateway still loading — render the skeleton with no footer actions, so
  // the fields below can seed their defaultValues from loaded data.
  if (isDeviceLoading && !hasCurrentDeviceData) {
    return (
      <Modal
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        maxW='600px'
        onClose={onClose}
        size='compact'
        title={t('gateways:crud_edit_title')}
      >
        <SkeletonModalBody />
      </Modal>
    )
  }

  const isFieldDisabled = isUpdateLoading || !isEditMode
  const labelColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={!shouldEnableUpdateGateway || isFieldDisabled}
      isSaveLoading={isUpdateLoading}
      maxW='600px'
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('common:save_changes')}
      showCancelButton
      size='compact'
      title={t('gateways:crud_edit_title')}
    >
      <Flex direction='column' gap='16px'>
        <ControlledTextInput
          colorScheme={colorScheme}
          control={control}
          data-testid='EditGatewayModal:name'
          defaultValue={deviceData?.edgeDevice?.name ?? ''}
          disabled={isFieldDisabled}
          errors={errors}
          label={t('gateways:crud_field_name_label')}
          name='name'
          placeholder={t('gateways:crud_field_name_placeholder')}
          rules={{ required: t('gateways:crud_field_name_required') }}
          width='100%'
        />

        <Box>
          <Flex alignItems='center' justifyContent='space-between' mb='4px'>
            <Text color={labelColor} textStyle='label'>
              {t('gateways:crud_field_devices_label')}
            </Text>
            <Flex gap='8px'>
              <Button
                colorScheme={colorScheme}
                data-testid='EditGatewayModal:clearAllDevices'
                disabled={
                  isFieldDisabled ||
                  isDevicesOptionsLoading ||
                  !selectedDevices?.length
                }
                onClick={() => setValue('devices', [])}
                size='sm'
                variant='tertiary'
              >
                {t('common:clear_all')}
              </Button>
              <Button
                colorScheme={colorScheme}
                data-testid='EditGatewayModal:selectAllDevices'
                // Select All while the unpaired-devices fetch is in flight
                // would set the field to the still-empty options list —
                // visually "selecting all" but actually clearing every
                // pairing.
                disabled={isFieldDisabled || isDevicesOptionsLoading}
                onClick={() => setValue('devices', devicesOptions)}
                size='sm'
                variant='tertiary'
              >
                {t('common:select_all')}
              </Button>
            </Flex>
          </Flex>
          <ControlledMultiSelectInput
            colorScheme={colorScheme}
            control={control}
            data-testid='EditGatewayModal:devices'
            defaultValue={devices}
            disabled={isFieldDisabled}
            maxDropdownHeight='200px'
            name='devices'
            options={devicesOptions}
            placeholder={t('gateways:crud_field_devices_placeholder')}
            resultsHeader={t('gateways:crud_field_devices_label')}
            searchable
            usePortal
          />
        </Box>
      </Flex>
    </Modal>
  )
}
